import React, { useState, useEffect } from 'react';
import DesignerSidebar from './DesignerSidebar';
import WidgetEditorPanel from './WidgetEditorPanel';
import ReportQueriesEditor from './ReportQueriesEditor';

/**
 * Single right dock with tabs: گزارشات | داده | ویجت
 * Auto-switches to ویجت when a widget is selected.
 */
export default function DesignerSidePanel({
  activeTab,
  onTabChange,
  // reports tab
  jobs,
  selectedJobId,
  onJobChange,
  reports,
  selectedReportId,
  onSelectReport,
  onAddReport,
  onDeleteReport,
  onDuplicateReport,
  onCopyReportToJob,
  onRenameReport,
  onReorderReports,
  onToggleReportVisible,
  onToggleMainDashboard,
  showCustomerTarget,
  onShowCustomerTarget,
  defaultReportId,
  onDefaultReportChange,
  useCustomAsMainDashboard,
  onUseCustomAsMainDashboard,
  mainDashboardReportId,
  onMainDashboardReportChange,
  isDirty,
  onSave,
  saving,
  saveError,
  validationErrors,
  // widget tab
  maxLevel = 4,
  selectedWidget,
  selectedLayout,
  onChangeWidget,
  onLayoutItemChange,
  onCloseWidget,
  selectedReport,
  onChangeReportQueries,
}) {
  const [tab, setTab] = useState(activeTab || 'reports');

  useEffect(() => {
    if (activeTab) setTab(activeTab);
  }, [activeTab]);

  const switchTab = (next) => {
    setTab(next);
    onTabChange?.(next);
  };

  return (
    <aside className="w-[360px] shrink-0 h-full flex flex-col border-l border-slate-200 bg-white shadow-[ -8px_0_24px_rgba(15,23,42,0.06)] min-h-0 z-20">
      <div className="shrink-0 flex border-b border-slate-200 bg-slate-50/90" dir="rtl">
        <button
          type="button"
          onClick={() => switchTab('reports')}
          className={`flex-1 px-2 py-2.5 text-xs font-bold transition-colors border-b-2 ${
            tab === 'reports'
              ? 'border-indigo-600 text-indigo-700 bg-white'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100/80'
          }`}
        >
          گزارشات
          {isDirty && (
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" title="تغییرات ذخیره‌نشده" />
          )}
        </button>
        <button
          type="button"
          onClick={() => switchTab('data')}
          disabled={!selectedReport}
          className={`flex-1 px-2 py-2.5 text-xs font-bold transition-colors border-b-2 ${
            tab === 'data'
              ? 'border-indigo-600 text-indigo-700 bg-white'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100/80'
          } disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent`}
        >
          داده
          {(selectedReport?.queries || []).length > 0 && (
            <span className="mr-1 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-bold text-indigo-700">
              {(selectedReport.queries || []).length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => switchTab('widget')}
          disabled={!selectedWidget}
          className={`flex-1 px-2 py-2.5 text-xs font-bold transition-colors border-b-2 ${
            tab === 'widget'
              ? 'border-indigo-600 text-indigo-700 bg-white'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100/80'
          } disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent`}
        >
          ویجت
          {selectedWidget && (
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-indigo-500 align-middle" />
          )}
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {tab === 'reports' ? (
          <DesignerSidebar
            embedded
            collapsed={false}
            onToggleCollapse={() => {}}
            jobs={jobs}
            selectedJobId={selectedJobId}
            onJobChange={onJobChange}
            reports={reports}
            selectedReportId={selectedReportId}
            onSelectReport={onSelectReport}
            onAddReport={onAddReport}
            onDeleteReport={onDeleteReport}
            onDuplicateReport={onDuplicateReport}
            onCopyReportToJob={onCopyReportToJob}
            onRenameReport={onRenameReport}
            onReorderReports={onReorderReports}
            onToggleReportVisible={onToggleReportVisible}
            onToggleMainDashboard={onToggleMainDashboard}
            showCustomerTarget={showCustomerTarget}
            onShowCustomerTarget={onShowCustomerTarget}
            defaultReportId={defaultReportId}
            onDefaultReportChange={onDefaultReportChange}
            useCustomAsMainDashboard={useCustomAsMainDashboard}
            onUseCustomAsMainDashboard={onUseCustomAsMainDashboard}
            mainDashboardReportId={mainDashboardReportId}
            onMainDashboardReportChange={onMainDashboardReportChange}
            isDirty={isDirty}
            onSave={onSave}
            saving={saving}
            saveError={saveError}
          />
        ) : tab === 'data' && selectedReport ? (
          <ReportQueriesEditor
            queries={selectedReport.queries || []}
            onChange={(queries) => onChangeReportQueries?.(queries)}
          />
        ) : selectedWidget ? (
          <WidgetEditorPanel
            widget={selectedWidget}
            maxLevel={maxLevel}
            jobs={jobs}
            layout={selectedLayout}
            onChange={onChangeWidget}
            onLayoutChange={onLayoutItemChange}
            validationErrors={validationErrors}
            reportQueries={selectedReport?.queries || []}
            onClose={() => {
              onCloseWidget?.();
              switchTab('reports');
            }}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center rtl text-slate-400">
            <span className="text-3xl opacity-50">✏️</span>
            <p className="text-sm font-medium text-slate-500">ویرایشگر ویجت</p>
            <p className="text-xs leading-relaxed">
              یک ویجت را از بوم انتخاب کنید تا خصوصیات آن اینجا نمایش داده شود
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}
